import { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import DashLayout from "@/components/DashLayout";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import {
  Plus, GripVertical, Copy, Trash2, Repeat, Save, Send, ChevronDown, ChevronRight,
  ClipboardList, BookOpen, Search, X, Check, PackageCheck, Eye, Video as CameraIcon,
  Edit3, Archive, ArchiveRestore, Loader2, History,
} from "lucide-react";
import { ExerciseThumb, ExerciseVideo } from "@/lib/exerciseMedia";
import ExerciseVideoUploader from "@/components/ExerciseVideoUploader";

/**
 * Clinician Exercise Library + Programme Builder (Phase 1).
 *
 * Three tabs:
 *   • Library — add exercises to the working programme
 *   • Builder — reorder / edit / assign the working programme
 *   • Templates — load or delete clinic-wide programme templates
 *
 * The working programme is held in local component state (draft) — it is only
 * persisted server-side when the clinician clicks either "Save as template"
 * or "Assign to patient".
 */

const EMPTY_EXERCISE = {
  sets: 3, reps: 10, rest_seconds: 60, frequency_per_week: 3,
  tempo: "", weight: "", duration_seconds: null,
  pain_limit: "", progression: "", regression: "", notes: "",
};

const FREQ_OPTIONS = ["Daily", "5× per week", "3× per week", "2× per week", "Weekly", "As tolerated"];

// Phase 1B-2.1 · Mandatory clinical fields required before Approve & Activate.
// Mirrors the backend gate in server.py (_APPROVAL_REQUIRED_FIELDS) so the UI
// can show the clinician exactly what still blocks approval.
const APPROVAL_REQUIRED = [
  ["name", "Name"], ["category", "Category"],
  ["instructions", "Instructions"],
  ["coaching_cues", "Coaching cues"], ["equipment", "Equipment"],
  ["safety_information", "Safety information"],
];

function exerciseMissingFields(ex) {
  return APPROVAL_REQUIRED.filter(([k]) => {
    const v = ex[k];
    if (k === "coaching_cues") return !(Array.isArray(v) && v.some((x) => String(x).trim()));
    return v == null || (typeof v === "string" && !v.trim());
  }).map(([, label]) => label);
}

function newDraftItem(libExercise) {
  return {
    _uid: `${libExercise.id}-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
    exercise_id: libExercise.id,
    exercise: libExercise, // client-side denorm for rendering
    ...EMPTY_EXERCISE,
    sets: libExercise.default_sets ?? 3,
    reps: libExercise.default_reps ?? 10,
  };
}

export default function ClinicianLibrary() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const canUploadVideo = user?.role === "clinician" || user?.role === "admin";
  const isClinicalAdmin = (user?.role === "admin" || !!user?.is_admin) && !!user?.clinical_admin;
  const [searchParams, setSearchParams] = useSearchParams();
  const [statusFilter, setStatusFilter] = useState(() => {
    const s = searchParams.get("status");
    return s === "pending" ? "pending" : s === "all" ? "all" : "ready";
  });
  const [videoTarget, setVideoTarget] = useState(null);

  // Data
  const [items, setItems] = useState([]);
  const [patients, setPatients] = useState([]);
  const [team, setTeam] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [revisions, setRevisions] = useState([]);

  // UI
  const [tab, setTab] = useState("library"); // library | builder | templates
  const [query, setQuery] = useState("");
  const [showArchived, setShowArchived] = useState(false);
  const [showLibForm, setShowLibForm] = useState(false);
  const [editingId, setEditingId] = useState(null);   // null = create; string = update
  const emptyLibForm = {
    name: "", category: "Lower body", description: "", video_url: "",
    video_public_id: "", video_duration: null, thumbnail_url: "",
    default_sets: 3, default_reps: 10, equipment: "", instructions: "",
    body_region: "", difficulty: "Beginner", phase: "Rehabilitation",
    coaching_cues: "",     // typed as newline-separated, stored as List[str]
    safety_information: "",
    progression_id: "", regression_id: "", archived: false,
  };
  const [libForm, setLibForm] = useState(emptyLibForm);

  // Draft (working programme)
  const [draft, setDraft] = useState({
    title: "", phase: "Rehabilitation", goal: "",
    start_date: "", end_date: "", completion_frequency: FREQ_OPTIONS[2],
    assigned_clinician_id: user?.id || "",
    exercises: [],
  });
  const [expanded, setExpanded] = useState({}); // { _uid: true }
  const dragUidRef = useRef(null);

  // Assign modal
  const [assignOpen, setAssignOpen] = useState(false);
  const [assignPatient, setAssignPatient] = useState("");
  const [saving, setSaving] = useState(false);

  // Save-template modal
  const [tplOpen, setTplOpen] = useState(false);
  const [tplName, setTplName] = useState("");

  // Preview-as-patient modal
  const [previewOpen, setPreviewOpen] = useState(false);

  // iter105 · Post-review-P0 · library pagination
  const PAGE_SIZE = 60;
  const [offset, setOffset] = useState(0);
  const [total, setTotal] = useState(0);

  const refresh = () => {
    const params = new URLSearchParams();
    if (showArchived) params.set("include_archived", "true");
    if (query.trim()) params.set("q", query.trim());
    if (statusFilter === "pending") params.set("review_status", "pending");
    else if (statusFilter === "ready") params.set("approved_only", "true");
    params.set("limit", String(PAGE_SIZE));
    params.set("offset", String(offset));
    api.get(`/exercises?${params}`).then((r) => {
      if (Array.isArray(r.data)) { setItems(r.data); setTotal(r.data.length); }
      else { setItems(r.data?.items || []); setTotal(r.data?.total || 0); }
    });
    api.get("/patients").then((r) => setPatients(r.data)).catch(() => {});
    api.get("/clinicians").then((r) => setTeam(r.data)).catch(() => {});
    api.get("/programme-templates").then((r) => setTemplates(r.data)).catch(() => {});
    api.get("/exercises/revisions/pending").then((r) => setRevisions(r.data?.revisions || [])).catch(() => {});
  };
  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [showArchived, offset, statusFilter]);

  // Debounced server-side search across the new fields. Reset page 0 on a
  // new query so pagination doesn't skip matches that fell outside the
  // previous offset.
  useEffect(() => {
    const h = setTimeout(() => {
      if (offset !== 0) setOffset(0);
      else refresh();
    }, 250);
    return () => clearTimeout(h);
    // eslint-disable-next-line
  }, [query]);

  useEffect(() => {
    setDraft((d) => ({ ...d, assigned_clinician_id: d.assigned_clinician_id || user?.id || "" }));
  }, [user?.id]);

  const filtered = useMemo(() => items, [items]);

  /* -------- Library form -------- */
  const openCreate = () => {
    setEditingId(null);
    setLibForm(emptyLibForm);
    setShowLibForm(true);
  };
  const openEdit = (ex) => {
    setEditingId(ex.id);
    setLibForm({
      ...emptyLibForm,
      ...ex,
      coaching_cues: (ex.coaching_cues || []).join("\n"),
      safety_information: ex.safety_information || "",
    });
    setShowLibForm(true);
  };
  const closeLibForm = () => {
    setShowLibForm(false); setEditingId(null); setLibForm(emptyLibForm);
  };

  const saveLibraryExercise = async (e) => {
    e.preventDefault();
    const payload = {
      ...libForm,
      default_sets: parseInt(libForm.default_sets) || 3,
      default_reps: parseInt(libForm.default_reps) || 10,
      coaching_cues: (libForm.coaching_cues || "")
        .split("\n").map((s) => s.trim()).filter(Boolean),
      progression_id: libForm.progression_id || null,
      regression_id: libForm.regression_id || null,
      video_duration: libForm.video_duration || null,
    };
    try {
      if (editingId) {
        await api.patch(`/exercises/${editingId}`, payload);
        toast.success("Exercise updated");
      } else {
        await api.post("/exercises", payload);
        toast.success("Exercise added to library");
      }
      closeLibForm();
      refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    }
  };

  const toggleArchive = async (ex) => {
    try {
      await api.patch(`/exercises/${ex.id}`, { archived: !ex.archived });
      toast.success(ex.archived ? "Restored from archive" : "Archived — hidden from Builder search");
      refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    }
  };

  // Phase 1B-2.1 · Approve & activate a draft via the dedicated endpoint.
  const approveExercise = async (ex) => {
    try {
      await api.post(`/exercises/${ex.id}/approve`);
      toast.success(`"${ex.name}" approved & activated`);
      refresh();
    } catch (err) {
      const d = err?.response?.data?.detail;
      if (d && d.missing_fields) {
        toast.error(`Cannot approve — complete: ${d.missing_fields.join(", ")}`);
      } else {
        toast.error(typeof d === "string" ? d : "Approval failed");
      }
    }
  };

  // Point 4 · Task 2 — independent review of a pending change to an approved
  // exercise. The live, patient-facing record stays unchanged until a DIFFERENT
  // clinical reviewer approves (SoD enforced server-side).
  const approveRevision = async (rev) => {
    try {
      await api.post(`/exercises/revisions/${rev.id}/approve`);
      toast.success(`Revision to "${rev.exercise_name}" approved & applied`);
      refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Approval failed");
    }
  };
  const rejectRevision = async (rev) => {
    if (!window.confirm("Reject this proposed change? The live exercise stays unchanged.")) return;
    try {
      await api.post(`/exercises/revisions/${rev.id}/reject`);
      toast.success("Revision rejected — live exercise unchanged");
      refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Reject failed");
    }
  };

  /* -------- Draft mutators -------- */
  const addToProgramme = (libExercise) => {
    setDraft((d) => ({ ...d, exercises: [...d.exercises, newDraftItem(libExercise)] }));
    toast.success(`"${libExercise.name}" added to programme`);
    // Auto-expand the newly added row
    setTimeout(() => {
      setExpanded((e) => ({ ...e, [`${libExercise.id}-newest`]: true }));
    }, 50);
  };

  const patchItem = (uid, patch) =>
    setDraft((d) => ({ ...d, exercises: d.exercises.map((x) => x._uid === uid ? { ...x, ...patch } : x) }));

  const removeItem = (uid) =>
    setDraft((d) => ({ ...d, exercises: d.exercises.filter((x) => x._uid !== uid) }));

  const duplicateItem = (uid) => {
    setDraft((d) => {
      const idx = d.exercises.findIndex((x) => x._uid === uid);
      if (idx < 0) return d;
      const copy = { ...d.exercises[idx], _uid: `${d.exercises[idx].exercise_id}-${Date.now()}-${Math.random().toString(36).slice(2, 6)}` };
      const next = [...d.exercises];
      next.splice(idx + 1, 0, copy);
      return { ...d, exercises: next };
    });
    toast.success("Exercise duplicated");
  };

  const replaceItem = (uid, newLibExercise) => {
    setDraft((d) => ({
      ...d,
      exercises: d.exercises.map((x) =>
        x._uid === uid
          ? { ...x, exercise_id: newLibExercise.id, exercise: newLibExercise }
          : x),
    }));
    toast.success(`Replaced with "${newLibExercise.name}"`);
  };

  const reorder = (fromUid, toUid) => {
    if (!fromUid || !toUid || fromUid === toUid) return;
    setDraft((d) => {
      const arr = [...d.exercises];
      const fromIdx = arr.findIndex((x) => x._uid === fromUid);
      const toIdx = arr.findIndex((x) => x._uid === toUid);
      if (fromIdx < 0 || toIdx < 0) return d;
      const [moved] = arr.splice(fromIdx, 1);
      arr.splice(toIdx, 0, moved);
      return { ...d, exercises: arr };
    });
  };

  const clearDraft = () => {
    if (draft.exercises.length && !window.confirm("Clear the current programme draft?")) return;
    setDraft({
      title: "", phase: "Rehabilitation", goal: "",
      start_date: "", end_date: "", completion_frequency: FREQ_OPTIONS[2],
      assigned_clinician_id: user?.id || "",
      exercises: [],
    });
    setExpanded({});
  };

  const loadTemplate = (tpl) => {
    if (draft.exercises.length && !window.confirm("Loading a template will replace the current draft. Continue?")) return;
    const libById = Object.fromEntries(items.map((x) => [x.id, x]));
    const exercises = (tpl.exercises || []).map((e) => ({
      ...EMPTY_EXERCISE,
      ...e,
      _uid: `${e.exercise_id}-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
      exercise: libById[e.exercise_id] || null,
    }));
    setDraft((d) => ({
      // Preserve any title the clinician has already typed; only fall back to
      // the template name when the draft title is empty.
      title: d.title?.trim() ? d.title : (tpl.name || ""),
      phase: tpl.phase || "Rehabilitation",
      goal: tpl.goal || d.goal || "",
      completion_frequency: tpl.completion_frequency || FREQ_OPTIONS[2],
      start_date: d.start_date || "",
      end_date: d.end_date || "",
      assigned_clinician_id: d.assigned_clinician_id || user?.id || "",
      exercises,
    }));
    setTab("builder");
    toast.success(`Loaded template "${tpl.name}"`);
  };

  /* -------- Persist -------- */
  const saveAsTemplate = async () => {
    if (!tplName.trim()) return toast.error("Give the template a name.");
    if (draft.exercises.length === 0) return toast.error("Add at least one exercise first.");
    try {
      await api.post("/programme-templates", {
        name: tplName.trim(),
        phase: draft.phase,
        goal: draft.goal,
        completion_frequency: draft.completion_frequency,
        exercises: draft.exercises.map(({ _uid, exercise, ...rest }) => rest),
      });
      toast.success(`Template "${tplName}" saved to the clinic library`);
      setTplOpen(false);
      setTplName("");
      const r = await api.get("/programme-templates");
      setTemplates(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save template");
    }
  };

  const removeTemplate = async (tpl) => {
    if (!window.confirm(`Delete template "${tpl.name}"?`)) return;
    try {
      await api.delete(`/programme-templates/${tpl.id}`);
      setTemplates((prev) => prev.filter((t) => t.id !== tpl.id));
      toast.success("Template deleted");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not delete");
    }
  };

  const assign = async () => {
    if (!assignPatient) return toast.error("Pick a patient");
    if (!draft.title.trim()) return toast.error("Give the programme a name");
    if (draft.exercises.length === 0) return toast.error("Add at least one exercise");
    setSaving(true);
    try {
      await api.post("/programmes", {
        patient_id: assignPatient,
        title: draft.title.trim(),
        phase: draft.phase,
        goal: draft.goal,
        start_date: draft.start_date || null,
        end_date: draft.end_date || null,
        completion_frequency: draft.completion_frequency || null,
        assigned_clinician_id: draft.assigned_clinician_id || null,
        exercises: draft.exercises.map(({ _uid, exercise, ...rest }) => rest),
      });
      toast.success("Programme assigned — patient has been notified");
      setAssignOpen(false);
      setAssignPatient("");
      clearDraft();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not assign");
    } finally { setSaving(false); }
  };

  const pdfUrl = (e) => e.video_url;

  return (
    <DashLayout>
      <div className="flex justify-between items-end mb-6 flex-wrap gap-3">
        <div>
          <div className="fl-label text-[#f47a20]">{t("pages.cLibrary.eyebrow")}</div>
          <h1 className="fl-heading text-4xl mt-2">Exercise Library & Programme Builder</h1>
        </div>
        {tab === "library" && (
          <div className="flex gap-2 flex-wrap">
            <label className="text-xs text-zinc-400 flex items-center gap-2">
              <input type="checkbox" checked={showArchived} onChange={(e) => setShowArchived(e.target.checked)}
                     data-testid="lib-show-archived" /> Show archived
            </label>
            <button className="fl-btn" onClick={showLibForm ? closeLibForm : openCreate} data-testid="lib-toggle">
              <Plus size={14} /> {showLibForm ? "Close form" : "Add exercise"}
            </button>
          </div>
        )}
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-6 border-b" style={{ borderColor: "var(--fl-border)" }}>
        {[
          ["library", `Library (${offset + 1}–${Math.min(offset + items.length, total)} of ${total.toLocaleString()})`, BookOpen],
          ["builder", `Programme Builder (${draft.exercises.length})`, ClipboardList],
          ["templates", `Templates (${templates.length})`, PackageCheck],
          ["revisions", `Pending revisions (${revisions.length})`, History],
        ].map(([k, l, Icon]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`lib-tab-${k}`}
            className={`px-4 py-3 text-sm uppercase tracking-wider font-semibold flex items-center gap-2 ${tab === k ? "text-[#f47a20] border-b-2 border-[#f47a20] -mb-px" : "text-zinc-500 hover:text-zinc-300"}`}>
            <Icon size={14} /> {l}
          </button>
        ))}
      </div>

      {/* ---------------- LIBRARY TAB ---------------- */}
      {tab === "library" && (
        <>
          {showLibForm && (
            <form onSubmit={saveLibraryExercise} className="fl-card p-6 mb-6 grid md:grid-cols-2 gap-3" data-testid="lib-form">
              <div className="md:col-span-2 fl-label text-[#f47a20]">
                {editingId ? "Edit exercise" : "New exercise"}
              </div>

              {/* Video source — single secure pathway (iter105).
                  Video upload requires an existing exercise (so the server
                  can derive `library_key` and sign `functional-lab/exercises/
                  {library_key}/{variant}`). For a new exercise, save first,
                  then re-open Edit to attach the demo clip. */}
              <div className="md:col-span-2 fl-card p-4 space-y-3" style={{ background: "var(--fl-panel-2, rgba(255,255,255,0.02))" }}>
                <div className="flex items-center justify-between gap-3">
                  <div className="fl-label">Demo video</div>
                  {editingId && canUploadVideo && (
                    <button
                      type="button"
                      onClick={() => setVideoTarget(items.find((x) => x.id === editingId) || { id: editingId, ...libForm })}
                      className="fl-btn text-xs"
                      data-testid="lib-video-manage"
                    >
                      <CameraIcon size={12} /> {libForm.video_public_id ? "Replace / add variant" : "Upload video"}
                    </button>
                  )}
                </div>

                {libForm.video_url && (
                  <ExerciseVideo ex={libForm} className="rounded max-h-56" />
                )}

                {libForm.video_public_id ? (
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-[10px] text-emerald-400">
                      Main variant: <code>{libForm.video_public_id}</code>
                    </span>
                    {libForm.video_duration ? (
                      <span className="text-[10px]" style={{ color: "var(--fl-muted-2)" }}>
                        · {Math.round(libForm.video_duration)}s
                      </span>
                    ) : null}
                  </div>
                ) : (
                  <div className="text-[11px]" style={{ color: "var(--fl-muted-2)" }}>
                    {editingId
                      ? "No video yet — click Upload video to attach the Main variant. You can add Front, Side, Regression, Progression, or Common-error variants from the same modal."
                      : "Save the exercise first, then re-open Edit to upload demo videos. The server generates the Cloudinary public_id from the exercise's library_key."}
                  </div>
                )}
              </div>

              <input className="fl-input" required placeholder="Name" value={libForm.name} onChange={(e) => setLibForm({ ...libForm, name: e.target.value })} data-testid="lib-name" />
              <input className="fl-input" placeholder="Category" value={libForm.category} onChange={(e) => setLibForm({ ...libForm, category: e.target.value })} />
              <input className="fl-input" placeholder="Equipment" value={libForm.equipment} onChange={(e) => setLibForm({ ...libForm, equipment: e.target.value })} />
              <select className="fl-input" value={libForm.difficulty}
                       onChange={(e) => setLibForm({ ...libForm, difficulty: e.target.value })}
                       data-testid="lib-difficulty">
                {["Beginner", "Intermediate", "Advanced"].map((v) => <option key={v}>{v}</option>)}
              </select>
              <select className="fl-input" value={libForm.phase}
                       onChange={(e) => setLibForm({ ...libForm, phase: e.target.value })}
                       data-testid="lib-phase">
                {["Foundation", "Rehabilitation", "Reconditioning", "Return-to-sport", "Performance", "Maintenance"].map((v) => <option key={v}>{v}</option>)}
              </select>
              <input type="number" className="fl-input" placeholder="Default sets" value={libForm.default_sets} onChange={(e) => setLibForm({ ...libForm, default_sets: e.target.value })} />
              <input type="number" className="fl-input" placeholder="Default reps" value={libForm.default_reps} onChange={(e) => setLibForm({ ...libForm, default_reps: e.target.value })} />

              <select className="fl-input" value={libForm.regression_id}
                       onChange={(e) => setLibForm({ ...libForm, regression_id: e.target.value })}
                       data-testid="lib-regression">
                <option value="">Regression (easier version) — none</option>
                {items.filter((x) => x.id !== editingId).map((x) => (
                  <option key={x.id} value={x.id}>{x.name}</option>
                ))}
              </select>
              <select className="fl-input" value={libForm.progression_id}
                       onChange={(e) => setLibForm({ ...libForm, progression_id: e.target.value })}
                       data-testid="lib-progression">
                <option value="">Progression (harder version) — none</option>
                {items.filter((x) => x.id !== editingId).map((x) => (
                  <option key={x.id} value={x.id}>{x.name}</option>
                ))}
              </select>

              <div className="md:col-span-2 fl-label text-[#f47a20] mt-1">Guidance</div>
              <div className="md:col-span-2">
                <textarea className="fl-input w-full" rows={3} placeholder="Instructions — short, actionable steps (numbered where sequence matters)" value={libForm.instructions} onChange={(e) => setLibForm({ ...libForm, instructions: e.target.value })} data-testid="lib-instructions" />
                <div className="text-[11px] text-zinc-500 mt-1">The primary steps patients see on My Plan.</div>
              </div>
              <div className="md:col-span-2">
                <textarea className="fl-input w-full font-mono text-xs"
                          placeholder="Coaching cues — one bullet per line (positioning, movement quality, common compensations)"
                          rows={3}
                          value={libForm.coaching_cues}
                          onChange={(e) => setLibForm({ ...libForm, coaching_cues: e.target.value })}
                          data-testid="lib-cues"/>
                <div className="text-[11px] text-zinc-500 mt-1">One cue per line. Keep them short — don't repeat the instructions.</div>
              </div>
              <div className="md:col-span-2">
                <textarea className="fl-input w-full"
                          placeholder="Safety information — precautions, contraindications, or 'No specific precautions identified'"
                          rows={3}
                          value={libForm.safety_information}
                          onChange={(e) => setLibForm({ ...libForm, safety_information: e.target.value })}
                          data-testid="lib-safety-information"/>
                <div className="text-[11px] text-zinc-500 mt-1">
                  Required before approval. Record exercise-specific precautions/contraindications, or explicitly state that none were identified.
                </div>
              </div>

              <div className="md:col-span-2 flex gap-2 justify-end">
                <button type="button" onClick={closeLibForm} className="fl-btn-ghost">Cancel</button>
                <button className="fl-btn" data-testid="lib-save">
                  {editingId ? "Save changes" : "Save exercise"}
                </button>
              </div>
            </form>
          )}

          <div className="relative mb-4 max-w-sm">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
            <input className="fl-input pl-9" placeholder="Search exercises…" value={query} onChange={(e) => setQuery(e.target.value)} data-testid="lib-search" />
          </div>

          <div className="flex items-center gap-2 mb-4 flex-wrap" data-testid="lib-status-filter">
            {[["ready", "Exercises"], ["pending", "Pending review"], ["all", "All"]].map(([val, label]) => (
              <button
                key={val}
                type="button"
                data-testid={`lib-filter-${val}`}
                onClick={() => {
                  setStatusFilter(val);
                  setOffset(0);
                  const next = new URLSearchParams(searchParams);
                  if (val === "ready") next.delete("status");
                  else next.set("status", val);
                  setSearchParams(next, { replace: true });
                }}
                className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${
                  statusFilter === val
                    ? "border-[#f47a20] text-[#f47a20] bg-[#f47a20]/10"
                    : "border-white/10 text-zinc-400 hover:text-zinc-200"
                }`}
              >
                {label}
              </button>
            ))}
          </div>

          <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4">
            {filtered.map((e) => {
              const inProgramme = draft.exercises.filter((x) => x.exercise_id === e.id).length;
              return (
                <div key={e.id} className={`fl-card overflow-hidden flex flex-col ${e.archived ? "opacity-60" : ""}`} data-testid={`lib-card-${e.id}`}>
                  <ExerciseThumb ex={e} className="w-full h-40" />
                  <div className="p-5 flex-1 flex flex-col">
                    <div className="flex items-center gap-2 flex-wrap">
                      <div className="fl-label">{e.category}</div>
                      {e.body_region && <span className="text-[10px] px-1.5 py-0.5 rounded border border-white/10 text-zinc-400">{e.body_region}</span>}
                      {e.difficulty && <span className="text-[10px] px-1.5 py-0.5 rounded border border-white/10 text-zinc-400">{e.difficulty}</span>}
                      {e.clinical_review_status === "approved" && e.active && (
                        <span
                          className="text-[10px] px-1.5 py-0.5 rounded font-semibold"
                          style={{ background: "color-mix(in oklab, #22C55E 15%, transparent)", color: "#86EFAC", border: "1px solid #22C55E44" }}
                          data-testid={`lib-badge-approved-${e.id}`}
                        >Approved</span>
                      )}
                      {e.clinical_review_status === "pending" && (
                        <span
                          className="text-[10px] px-1.5 py-0.5 rounded font-semibold"
                          style={{ background: "color-mix(in oklab, #F59E0B 15%, transparent)", color: "#FCD34D", border: "1px solid #F59E0B44" }}
                          data-testid={`lib-badge-pending-${e.id}`}
                        >Pending review</span>
                      )}
                      {e.clinical_review_status === "rejected" && (
                        <span
                          className="text-[10px] px-1.5 py-0.5 rounded font-semibold"
                          style={{ background: "color-mix(in oklab, #EF4444 15%, transparent)", color: "#FCA5A5", border: "1px solid #EF444444" }}
                        >Rejected</span>
                      )}
                      {e.ai_eligible && (
                        <span
                          className="text-[10px] px-1.5 py-0.5 rounded font-semibold"
                          style={{ background: "color-mix(in oklab, #8B5CF6 15%, transparent)", color: "#C4B5FD", border: "1px solid #8B5CF644" }}
                        >AI-eligible</span>
                      )}
                      {e.archived && <span className="text-[10px] px-1.5 py-0.5 rounded border border-amber-500/40 text-amber-400">Archived</span>}
                    </div>
                    <div className="fl-heading text-lg mt-2">{e.name}</div>
                    <div className="text-sm text-zinc-400 mt-1 flex-1 line-clamp-2">{e.description}</div>
                    <div className="text-xs text-zinc-500 mt-2">Default: {e.default_sets} × {e.default_reps}{e.equipment ? ` · ${e.equipment}` : ""}</div>
                    <div className="flex items-center gap-2 mt-3 flex-wrap">
                      <button
                        className="fl-btn text-xs flex-1 justify-center"
                        onClick={() => addToProgramme(e)}
                        data-testid={`lib-add-${e.id}`}
                        title={
                          e.clinical_review_status && e.clinical_review_status !== "approved"
                            ? "This exercise must be marked Approved + Active before you can assign it"
                            : "Add to the current programme draft"
                        }
                        disabled={
                          e.archived ||
                          (e.clinical_review_status !== undefined &&
                            !(e.clinical_review_status === "approved" && e.active))
                        }
                      >
                        <Plus size={12} /> Add to programme
                      </button>
                      <button className="fl-btn-ghost text-xs" onClick={() => openEdit(e)} title="Edit"
                              data-testid={`lib-edit-${e.id}`}>
                        <Edit3 size={12} />
                      </button>
                      {canUploadVideo && (
                        <button
                          className="fl-btn-ghost text-xs"
                          onClick={() => setVideoTarget(e)}
                          title="Upload demo video (Main / Front / Side / …)"
                          data-testid={`lib-upload-video-${e.id}`}
                        >
                          <CameraIcon size={12} /> Upload video
                        </button>
                      )}
                      <button className="fl-btn-ghost text-xs" onClick={() => toggleArchive(e)}
                              title={e.archived ? "Restore from archive" : "Archive"}
                              data-testid={`lib-archive-${e.id}`}>
                        {e.archived ? <ArchiveRestore size={12}/> : <Archive size={12}/>}
                      </button>
                      {inProgramme > 0 && (
                        <span className="text-[10px] text-[#f47a20] font-semibold w-full">
                          {inProgramme}× in draft
                        </span>
                      )}
                      {isClinicalAdmin && e.clinical_review_status === "pending" && !e.archived && (
                        <div className="w-full mt-2 pt-2 border-t border-white/5">
                          {(() => {
                            const missing = exerciseMissingFields(e);
                            const mine = e.created_by_id === user?.id;
                            const canSelf = !!user?.can_self_approve_exercises;
                            const selfApprove = mine && canSelf;
                            const blocked = (mine && !canSelf) || missing.length > 0;
                            return (
                              <>
                                {missing.length > 0 && (
                                  <div className="text-[10px] text-amber-400 mb-1" data-testid={`lib-missing-${e.id}`}>
                                    Complete before approval: {missing.join(", ")}
                                  </div>
                                )}
                                <button
                                  className="fl-btn text-xs w-full justify-center"
                                  data-testid={`lib-approve-${e.id}`}
                                  disabled={blocked}
                                  title={(mine && !canSelf)
                                    ? "You created this exercise — a different clinical reviewer must approve it"
                                    : selfApprove
                                      ? "Self-review: approve your own exercise (recorded distinctly as self-review)"
                                      : (missing.length ? "Complete the mandatory clinical fields first" : "Approve & activate this exercise")}
                                  onClick={() => approveExercise(e)}
                                >
                                  <Check size={12} /> {selfApprove ? "Self-Review & Approve" : "Approve & Activate"}
                                </button>
                                {mine && !canSelf && (
                                  <div className="text-[10px] text-zinc-500 mt-1" data-testid={`lib-sod-note-${e.id}`}>
                                    You created this draft — a different clinical reviewer must sign it off.
                                  </div>
                                )}
                                {selfApprove && (
                                  <div className="text-[10px] text-amber-400/80 mt-1" data-testid={`lib-self-review-note-${e.id}`}>
                                    Authorised owner self-review — recorded separately from independent approval.
                                  </div>
                                )}
                              </>
                            );
                          })()}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
            {filtered.length === 0 && <div className="col-span-full text-sm text-zinc-500 p-6">No exercises match "{query}"</div>}
          </div>
          {total > PAGE_SIZE && (
            <div className="mt-6 flex items-center justify-between gap-3 flex-wrap"
                 data-testid="lib-pagination">
              <div className="text-xs text-zinc-500">
                Showing <span className="text-zinc-200 font-semibold">{offset + 1}</span>
                –<span className="text-zinc-200 font-semibold">{Math.min(offset + items.length, total)}</span>
                {" "}of <span className="text-zinc-200 font-semibold">{total.toLocaleString()}</span> exercises
              </div>
              <div className="flex gap-2">
                <button
                  className="fl-btn-ghost text-xs disabled:opacity-40"
                  disabled={offset === 0}
                  onClick={() => setOffset((o) => Math.max(0, o - PAGE_SIZE))}
                  data-testid="lib-page-prev"
                >‹ Prev</button>
                <button
                  className="fl-btn-ghost text-xs disabled:opacity-40"
                  disabled={offset + PAGE_SIZE >= total}
                  onClick={() => setOffset((o) => o + PAGE_SIZE)}
                  data-testid="lib-page-next"
                >Next ›</button>
              </div>
            </div>
          )}
        </>
      )}

      {/* ---------------- BUILDER TAB ---------------- */}
      {tab === "builder" && (
        <div className="space-y-4">
          {/* Programme header form */}
          <div className="fl-card p-5" data-testid="builder-header">
            <div className="fl-label mb-3 text-[#f47a20]">Programme details</div>
            <div className="grid md:grid-cols-3 gap-3">
              <input className="fl-input md:col-span-2" placeholder="Programme name — e.g. Post-ACL Phase 2" value={draft.title} onChange={(e) => setDraft({ ...draft, title: e.target.value })} data-testid="builder-title" />
              <select className="fl-input" value={draft.phase} onChange={(e) => setDraft({ ...draft, phase: e.target.value })} data-testid="builder-phase">
                {["Foundation", "Rehabilitation", "Reconditioning", "Return-to-sport", "Performance", "Maintenance"].map((p) => <option key={p}>{p}</option>)}
              </select>
              <input className="fl-input md:col-span-3" placeholder="Goals — e.g. Return to running pain-free by month 3" value={draft.goal} onChange={(e) => setDraft({ ...draft, goal: e.target.value })} data-testid="builder-goal" />
              <label className="flex flex-col gap-1">
                <span className="fl-label">Start date</span>
                <input type="date" className="fl-input" value={draft.start_date} onChange={(e) => setDraft({ ...draft, start_date: e.target.value })} data-testid="builder-start" />
              </label>
              <label className="flex flex-col gap-1">
                <span className="fl-label">End / review date</span>
                <input type="date" className="fl-input" value={draft.end_date} onChange={(e) => setDraft({ ...draft, end_date: e.target.value })} data-testid="builder-end" />
              </label>
              <label className="flex flex-col gap-1">
                <span className="fl-label">Completion frequency</span>
                <select className="fl-input" value={draft.completion_frequency} onChange={(e) => setDraft({ ...draft, completion_frequency: e.target.value })} data-testid="builder-frequency">
                  {FREQ_OPTIONS.map((f) => <option key={f}>{f}</option>)}
                </select>
              </label>
              <label className="flex flex-col gap-1 md:col-span-3">
                <span className="fl-label">Assigned clinician</span>
                <select className="fl-input" value={draft.assigned_clinician_id} onChange={(e) => setDraft({ ...draft, assigned_clinician_id: e.target.value })} data-testid="builder-clinician">
                  {team.map((c) => <option key={c.id} value={c.id}>{c.name}{c.id === user?.id ? " (you)" : ""}</option>)}
                </select>
              </label>
            </div>
          </div>

          {/* Exercises list */}
          <div className="fl-card overflow-hidden" data-testid="builder-list">
            <div className="p-4 border-b flex items-center justify-between flex-wrap gap-2" style={{ borderColor: "var(--fl-border)" }}>
              <div className="fl-label">Exercises ({draft.exercises.length}) — drag to reorder</div>
              <div className="flex gap-2 flex-wrap">
                <button onClick={clearDraft} className="fl-btn-ghost text-xs" data-testid="builder-clear">
                  <X size={12} /> Clear
                </button>
                <button
                  onClick={() => setPreviewOpen(true)}
                  className="fl-btn-ghost text-xs"
                  disabled={draft.exercises.length === 0}
                  title="See exactly what the patient will see on their /app/plan"
                  data-testid="builder-preview"
                >
                  <Eye size={12} /> Preview as patient
                </button>
                <button onClick={() => setTplOpen(true)} className="fl-btn-ghost text-xs" disabled={draft.exercises.length === 0} data-testid="builder-save-template">
                  <Save size={12} /> Save as template
                </button>
                <button onClick={() => setAssignOpen(true)} className="fl-btn text-xs" disabled={draft.exercises.length === 0 || !draft.title.trim()} data-testid="builder-assign">
                  <Send size={12} /> Assign to patient
                </button>
              </div>
            </div>
            {draft.exercises.length === 0 ? (
              <div className="p-8 text-center text-sm text-zinc-500">
                Programme is empty. Go to the <b>Library</b> tab and click <b>Add to programme</b> on any exercise.
              </div>
            ) : (
              <div className="divide-y" style={{ borderColor: "var(--fl-border)" }}>
                {draft.exercises.map((it, idx) => (
                  <BuilderRow
                    key={it._uid}
                    item={it}
                    index={idx}
                    libraryItems={items}
                    expanded={!!expanded[it._uid]}
                    onToggle={() => setExpanded({ ...expanded, [it._uid]: !expanded[it._uid] })}
                    onPatch={(patch) => patchItem(it._uid, patch)}
                    onRemove={() => removeItem(it._uid)}
                    onDuplicate={() => duplicateItem(it._uid)}
                    onReplace={(ex) => replaceItem(it._uid, ex)}
                    onDragStart={() => { dragUidRef.current = it._uid; }}
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={() => { reorder(dragUidRef.current, it._uid); dragUidRef.current = null; }}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* ---------------- TEMPLATES TAB ---------------- */}
      {tab === "templates" && (
        <div className="fl-card overflow-hidden" data-testid="templates-list">
          {templates.length === 0 ? (
            <div className="p-8 text-center text-sm text-zinc-500">
              No templates yet. Build a programme in the <b>Builder</b> tab and click <b>Save as template</b>.
            </div>
          ) : (
            <div className="divide-y" style={{ borderColor: "var(--fl-border)" }}>
              {templates.map((tpl) => (
                <div key={tpl.id} className="p-4 flex items-start justify-between gap-4" data-testid={`template-row-${tpl.id}`}>
                  <div className="min-w-0 flex-1">
                    <div className="fl-heading text-base">{tpl.name}</div>
                    <div className="text-xs text-zinc-400 mt-1">
                      {tpl.phase}{tpl.completion_frequency ? ` · ${tpl.completion_frequency}` : ""} · {tpl.exercises?.length || 0} exercise{tpl.exercises?.length === 1 ? "" : "s"}
                    </div>
                    {tpl.goal && <div className="text-xs text-zinc-500 mt-1 italic">"{tpl.goal}"</div>}
                    <div className="text-[10px] text-zinc-500 mt-1">By {tpl.created_by_name} · {tpl.created_at ? new Date(tpl.created_at).toLocaleDateString() : ""}</div>
                  </div>
                  <div className="flex gap-2 shrink-0">
                    <button onClick={() => loadTemplate(tpl)} className="fl-btn text-xs" data-testid={`template-load-${tpl.id}`}>
                      Load into builder
                    </button>
                    <button
                      onClick={() => removeTemplate(tpl)}
                      className="fl-btn-ghost text-xs text-red-400 hover:text-red-300"
                      title={user?.role === "admin" || tpl.created_by_id === user?.id ? "Delete template" : "Only the creator or an admin can delete"}
                      data-testid={`template-remove-${tpl.id}`}
                    >
                      <Trash2 size={12} /> Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ---------------- PENDING REVISIONS TAB ---------------- */}
      {tab === "revisions" && (
        <div className="fl-card overflow-hidden" data-testid="revisions-list">
          <div className="p-4 border-b" style={{ borderColor: "var(--fl-border)" }}>
            <div className="fl-label text-[#f47a20]">Approved-exercise change protection</div>
            <div className="text-xs text-zinc-400 mt-1 max-w-2xl">
              When clinically important content of an <b>already-approved</b> exercise is edited, the previous
              version is preserved and the change is held here as a pending revision. The live, patient-facing
              exercise stays unchanged until a <b>different</b> clinical reviewer approves it.
            </div>
          </div>
          {revisions.length === 0 ? (
            <div className="p-8 text-center text-sm text-zinc-500">No pending revisions.</div>
          ) : (
            <div className="divide-y" style={{ borderColor: "var(--fl-border)" }}>
              {revisions.map((rev) => {
                const changes = rev.proposed_changes || {};
                const prev = rev.previous_values || {};
                const mine = rev.author_id === user?.id;
                const canAct = isClinicalAdmin && (rev.eligible_to_review ?? (!mine));
                const fmt = (v) => Array.isArray(v) ? v.join(", ") : (v == null || v === "" ? "—" : String(v));
                return (
                  <div key={rev.id} className="p-4" data-testid={`revision-row-${rev.id}`}>
                    <div className="flex items-start justify-between gap-4 flex-wrap">
                      <div className="min-w-0 flex-1">
                        <div className="fl-heading text-base">{rev.exercise_name || "(exercise)"}</div>
                        <div className="text-[10px] text-zinc-500 mt-0.5">
                          Proposed by {rev.author_name || "—"} · {rev.updated_at || rev.created_at ? new Date(rev.updated_at || rev.created_at).toLocaleString() : ""} · {Object.keys(changes).length} field{Object.keys(changes).length === 1 ? "" : "s"}
                        </div>
                      </div>
                      <div className="flex gap-2 shrink-0">
                        <button
                          onClick={() => approveRevision(rev)}
                          className="fl-btn text-xs inline-flex items-center gap-1"
                          data-testid={`revision-approve-${rev.id}`}
                          disabled={!canAct}
                          title={!isClinicalAdmin ? "Only a clinical reviewer may approve" : (mine ? "You proposed this change — a different clinical reviewer must approve it" : "Approve & apply this change to the live exercise")}
                        >
                          <Check size={12} /> Approve
                        </button>
                        <button
                          onClick={() => rejectRevision(rev)}
                          className="fl-btn-ghost text-xs text-red-400 hover:text-red-300 inline-flex items-center gap-1"
                          data-testid={`revision-reject-${rev.id}`}
                          disabled={!canAct}
                          title={!isClinicalAdmin ? "Only a clinical reviewer may reject" : (mine ? "You proposed this change — a different clinical reviewer must action it" : "Reject this change — live exercise stays unchanged")}
                        >
                          <X size={12} /> Reject
                        </button>
                      </div>
                    </div>
                    {mine && (
                      <div className="text-[10px] text-zinc-500 mt-1" data-testid={`revision-sod-note-${rev.id}`}>
                        You proposed this change — a different clinical reviewer must sign it off.
                      </div>
                    )}
                    <div className="mt-3 space-y-2">
                      {Object.keys(changes).map((k) => (
                        <div key={k} className="text-xs bg-white/[0.03] rounded-md p-2 border border-white/5" data-testid={`revision-field-${rev.id}-${k}`}>
                          <div className="fl-label mb-1">{k.replace(/_/g, " ")}</div>
                          <div className="flex flex-col gap-0.5">
                            <span className="text-red-300/80 line-through break-words">{fmt(prev[k])}</span>
                            <span className="text-[#f47a20] break-words">{fmt(changes[k])}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* --------- Save-as-template modal --------- */}
      {tplOpen && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={() => setTplOpen(false)}>
          <div className="fl-card p-6 w-full max-w-md" onClick={(e) => e.stopPropagation()} data-testid="template-save-modal">
            <div className="flex items-center justify-between mb-3">
              <div className="fl-heading text-xl">Save as template</div>
              <button onClick={() => setTplOpen(false)}><X size={16} /></button>
            </div>
            <div className="text-xs text-zinc-400 mb-3">Templates are shared clinic-wide — every clinician can load them.</div>
            <input className="fl-input mb-4" placeholder="Template name" value={tplName} onChange={(e) => setTplName(e.target.value)} data-testid="template-name" autoFocus />
            <div className="flex justify-end gap-2">
              <button onClick={() => setTplOpen(false)} className="fl-btn-ghost text-sm">Cancel</button>
              <button onClick={saveAsTemplate} className="fl-btn text-sm" data-testid="template-save-confirm"><Save size={12} /> Save template</button>
            </div>
          </div>
        </div>
      )}

      {/* --------- Assign modal --------- */}
      {assignOpen && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={() => setAssignOpen(false)}>
          <div className="fl-card p-6 w-full max-w-md" onClick={(e) => e.stopPropagation()} data-testid="assign-modal">
            <div className="flex items-center justify-between mb-3">
              <div className="fl-heading text-xl">Assign to patient</div>
              <button onClick={() => setAssignOpen(false)}><X size={16} /></button>
            </div>
            <div className="text-xs text-zinc-400 mb-3">
              <b>{draft.title || "(no name)"}</b> · {draft.exercises.length} exercise{draft.exercises.length === 1 ? "" : "s"}{draft.completion_frequency ? ` · ${draft.completion_frequency}` : ""}
            </div>
            <div className="fl-label mb-1">Patient</div>
            <select className="fl-input mb-4" value={assignPatient} onChange={(e) => setAssignPatient(e.target.value)} data-testid="assign-patient">
              <option value="">— select —</option>
              {patients.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
            <div className="flex justify-end gap-2">
              <button onClick={() => setAssignOpen(false)} className="fl-btn-ghost text-sm">Cancel</button>
              <button onClick={assign} className="fl-btn text-sm" disabled={saving || !assignPatient} data-testid="assign-confirm">
                <Send size={12} /> {saving ? "Assigning…" : "Assign & notify"}
              </button>
            </div>
          </div>
        </div>
      )}
      {/* --------- Preview-as-patient modal --------- */}
      {previewOpen && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={() => setPreviewOpen(false)}>
          <div
            className="fl-card w-full max-w-2xl max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
            data-testid="preview-modal"
          >
            <div className="sticky top-0 flex items-center justify-between p-5 border-b" style={{ background: "var(--fl-panel)", borderColor: "var(--fl-border)" }}>
              <div>
                <div className="fl-label text-[#f47a20] flex items-center gap-1"><Eye size={11} /> Read-only preview · as the patient sees it</div>
                <div className="fl-heading text-xl mt-1">{draft.title || "(unnamed programme)"}</div>
              </div>
              <button onClick={() => setPreviewOpen(false)} data-testid="preview-close"><X size={18} /></button>
            </div>

            <div className="p-5 space-y-5">
              {/* Programme header — mirrors what patient sees at top of /app/plan */}
              <div className="fl-card p-4">
                <div className="fl-label">{draft.phase || "Rehabilitation"}</div>
                {draft.goal && <div className="text-sm text-zinc-300 mt-1">{draft.goal}</div>}
                <div className="flex flex-wrap gap-x-4 gap-y-1 mt-3 text-xs text-zinc-400">
                  {draft.completion_frequency && <span>Frequency: <b>{draft.completion_frequency}</b></span>}
                  {draft.start_date && <span>Start: <b>{draft.start_date}</b></span>}
                  {draft.end_date && <span>Review: <b>{draft.end_date}</b></span>}
                </div>
              </div>

              {/* Exercise list — read-only cards using the same visual language as the patient plan */}
              <div className="space-y-3">
                {draft.exercises.length === 0 && (
                  <div className="text-sm text-zinc-500 text-center p-6">No exercises added yet.</div>
                )}
                {draft.exercises.map((it, idx) => {
                  const ex = it.exercise || {};
                  return (
                    <div key={it._uid} className="fl-card p-4" data-testid={`preview-item-${it._uid}`}>
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0 flex-1">
                          <div className="fl-label">#{idx + 1} · {ex.category || "Exercise"}</div>
                          <div className="fl-heading text-lg mt-1">{ex.name || "(exercise removed from library)"}</div>
                          {ex.description && <div className="text-xs text-zinc-400 mt-1 line-clamp-3">{ex.description}</div>}
                        </div>
                        {ex.video_url && (
                          <img src={ex.video_url} alt="" className="w-20 h-20 object-cover rounded-md border" style={{ borderColor: "var(--fl-border)" }} />
                        )}
                      </div>

                      {/* Prescription pills — only render the fields that were actually set */}
                      <div className="flex flex-wrap gap-2 mt-3 text-[11px]">
                        <span className="px-2 py-1 rounded-full" style={{ background: "color-mix(in oklab, #f47a20 12%, transparent)", color: "#f47a20" }}>{it.sets} × {it.reps}</span>
                        {it.tempo && <Pill>Tempo {it.tempo}</Pill>}
                        {it.rest_seconds != null && <Pill>Rest {it.rest_seconds}s</Pill>}
                        {it.weight && <Pill>{it.weight}</Pill>}
                        {it.duration_seconds ? <Pill>{it.duration_seconds}s hold</Pill> : null}
                        {it.frequency_per_week && <Pill>{it.frequency_per_week}/wk</Pill>}
                        {it.pain_limit && <Pill>Pain ≤ {it.pain_limit}</Pill>}
                      </div>

                      {(it.progression || it.regression || it.notes) && (
                        <div className="mt-3 space-y-2 text-xs">
                          {it.progression && (
                            <div className="p-2 rounded-md" style={{ background: "color-mix(in oklab, #22C55E 7%, transparent)", border: "1px solid #22C55E33" }}>
                              <b className="text-emerald-400">Progression:</b> {it.progression}
                            </div>
                          )}
                          {it.regression && (
                            <div className="p-2 rounded-md" style={{ background: "color-mix(in oklab, #F59E0B 7%, transparent)", border: "1px solid #F59E0B33" }}>
                              <b className="text-amber-400">Regression:</b> {it.regression}
                            </div>
                          )}
                          {it.notes && (
                            <div className="p-2 rounded-md" style={{ background: "var(--fl-panel-2, transparent)", border: "1px solid var(--fl-border)" }}>
                              <b>Clinician note:</b> <span className="text-zinc-400">{it.notes}</span>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              <div className="text-[11px] text-center pt-2" style={{ color: "var(--fl-muted-2)" }}>
                This is a preview. Nothing has been saved or sent to the patient yet.
              </div>
            </div>

            <div className="sticky bottom-0 p-4 border-t flex justify-end gap-2" style={{ background: "var(--fl-panel)", borderColor: "var(--fl-border)" }}>
              <button onClick={() => setPreviewOpen(false)} className="fl-btn-ghost text-sm" data-testid="preview-close-bottom">Close preview</button>
              <button
                onClick={() => { setPreviewOpen(false); setAssignOpen(true); }}
                className="fl-btn text-sm"
                disabled={draft.exercises.length === 0 || !draft.title.trim()}
                data-testid="preview-goto-assign"
              >
                <Send size={12} /> Looks good — assign
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Video uploader — clinician/admin only. Endpoint role gate is
          `require_role("clinician", "admin")` server-side too. */}
      {videoTarget && canUploadVideo && (
        <ExerciseVideoUploader
          exercise={videoTarget}
          onDone={(doc) => {
            // Refresh the local card in-place so the thumbnail updates.
            setItems((prev) => prev.map((x) => x.id === doc.id ? doc : x));
            setVideoTarget(doc); // keep modal open, show success block
            // If the edit form is open for this exercise, mirror the new
            // main-variant fields so the "Replace" button label + preview
            // stay in sync without waiting for a refresh().
            if (editingId === doc.id) {
              setLibForm((f) => ({
                ...f,
                video_url: doc.video_url || f.video_url,
                video_public_id: doc.video_public_id || f.video_public_id,
                video_duration: doc.video_duration ?? f.video_duration,
                thumbnail_url: doc.thumbnail_url || f.thumbnail_url,
              }));
            }
          }}
          onClose={() => setVideoTarget(null)}
        />
      )}
    </DashLayout>
  );
}

// Tiny helper used inside the preview modal only — kept local so it doesn't
// pollute the exports of this page.
function Pill({ children }) {
  return (
    <span
      className="px-2 py-1 rounded-full"
      style={{ background: "var(--fl-panel-2, rgba(255,255,255,0.04))", border: "1px solid var(--fl-border)", color: "var(--fl-text-2)" }}
    >
      {children}
    </span>
  );
}

/* -------------------------------------------------------------
 * BuilderRow — a draggable, expandable card for one prescribed
 * exercise. Handles per-exercise fields (sets/reps/tempo/rest/
 * weight/duration/frequency/pain-limit/progression/regression/
 * notes) plus Duplicate / Remove / Replace controls.
 * -------------------------------------------------------------*/
function BuilderRow({ item, index, libraryItems, expanded, onToggle, onPatch, onRemove, onDuplicate, onReplace, onDragStart, onDragOver, onDrop }) {
  const [replaceOpen, setReplaceOpen] = useState(false);
  const [replaceQuery, setReplaceQuery] = useState("");
  const ex = item.exercise || {};
  const weekdays = Array.isArray(item.scheduled_weekdays) ? item.scheduled_weekdays : [];

  const toggleWeekday = (iso) => {
    const set = new Set(weekdays);
    if (set.has(iso)) set.delete(iso);
    else set.add(iso);
    onPatch({ scheduled_weekdays: Array.from(set).sort((a, b) => a - b) });
  };

  const replaceFiltered = useMemo(() => {
    const s = replaceQuery.trim().toLowerCase();
    if (!s) return libraryItems.slice(0, 40);
    return libraryItems.filter((e) => (e.name || "").toLowerCase().includes(s)).slice(0, 40);
  }, [libraryItems, replaceQuery]);

  return (
    <div
      className="p-4 hover:bg-white/[0.02]"
      draggable
      onDragStart={onDragStart}
      onDragOver={onDragOver}
      onDrop={onDrop}
      data-testid={`builder-row-${item._uid}`}
    >
      <div className="flex items-start gap-3">
        <button
          type="button"
          className="mt-1 cursor-grab active:cursor-grabbing text-zinc-500 hover:text-zinc-300"
          title="Drag to reorder"
          data-testid={`builder-drag-${item._uid}`}
        >
          <GripVertical size={16} />
        </button>
        <button onClick={onToggle} className="mt-0.5" data-testid={`builder-expand-${item._uid}`}>
          {expanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
        </button>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-[10px] text-zinc-500 font-mono">#{index + 1}</span>
            <div className="fl-heading text-base truncate">{ex.name || "(exercise removed from library)"}</div>
            {ex.category && <span className="text-[10px] fl-label">{ex.category}</span>}
          </div>
          <div className="text-[11px] text-zinc-500 mt-0.5">
            {item.sets}×{item.reps} · rest {item.rest_seconds}s · {item.frequency_per_week}/wk
            {item.tempo ? ` · tempo ${item.tempo}` : ""}
            {item.weight ? ` · ${item.weight}` : ""}
            {item.duration_seconds ? ` · ${item.duration_seconds}s hold` : ""}
            {item.pain_limit ? ` · pain ≤ ${item.pain_limit}` : ""}
          </div>
        </div>
        <div className="flex gap-1 shrink-0">
          <button onClick={onDuplicate} title="Duplicate" className="fl-btn-ghost text-xs" data-testid={`builder-duplicate-${item._uid}`}>
            <Copy size={12} />
          </button>
          <button onClick={() => setReplaceOpen(true)} title="Replace exercise" className="fl-btn-ghost text-xs" data-testid={`builder-replace-${item._uid}`}>
            <Repeat size={12} />
          </button>
          <button onClick={onRemove} title="Remove from programme" className="fl-btn-ghost text-xs text-red-400 hover:text-red-300" data-testid={`builder-remove-${item._uid}`}>
            <Trash2 size={12} />
          </button>
        </div>
      </div>

      {expanded && (
        <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-3 pl-10">
          <FieldNum label="Sets" value={item.sets} onChange={(v) => onPatch({ sets: v })} testid={`bf-sets-${item._uid}`} />
          <FieldNum label="Reps" value={item.reps} onChange={(v) => onPatch({ reps: v })} testid={`bf-reps-${item._uid}`} />
          <FieldStr label="Tempo" value={item.tempo} onChange={(v) => onPatch({ tempo: v })} placeholder="3-1-1-0" testid={`bf-tempo-${item._uid}`} />
          <FieldNum label="Rest (s)" value={item.rest_seconds} onChange={(v) => onPatch({ rest_seconds: v })} testid={`bf-rest-${item._uid}`} />
          <FieldStr label="Weight / resistance" value={item.weight} onChange={(v) => onPatch({ weight: v })} placeholder="12 kg / band red" testid={`bf-weight-${item._uid}`} />
          <FieldNum label="Duration (s)" value={item.duration_seconds || ""} onChange={(v) => onPatch({ duration_seconds: v || null })} testid={`bf-duration-${item._uid}`} />
          <FieldNum label="Frequency / week" value={item.frequency_per_week} onChange={(v) => onPatch({ frequency_per_week: v })} testid={`bf-frequency-${item._uid}`} />
          <FieldStr label="Pain / symptom limit" value={item.pain_limit} onChange={(v) => onPatch({ pain_limit: v })} placeholder="≤ 3/10" testid={`bf-painlimit-${item._uid}`} />
          <FieldTextarea className="col-span-2 md:col-span-2" label="Progression" value={item.progression} onChange={(v) => onPatch({ progression: v })} placeholder="+2 reps next week if pain-free" testid={`bf-progression-${item._uid}`} />
          <FieldTextarea className="col-span-2 md:col-span-2" label="Regression" value={item.regression} onChange={(v) => onPatch({ regression: v })} placeholder="Drop to bodyweight if pain > 4/10" testid={`bf-regression-${item._uid}`} />
          <FieldTextarea className="col-span-2 md:col-span-4" label="Clinician notes" value={item.notes} onChange={(v) => onPatch({ notes: v })} placeholder="Cue neutral spine, exhale on effort" testid={`bf-notes-${item._uid}`} />
          <div className="col-span-2 md:col-span-4">
            <div className="fl-label mb-2">
              Scheduled days
              <span className="text-[10px] font-normal ml-2 text-zinc-500">
                (empty = auto-distribute {item.frequency_per_week}× per week from start date)
              </span>
            </div>
            <div className="flex gap-1.5 flex-wrap">
              {[
                [1, "Mon"], [2, "Tue"], [3, "Wed"], [4, "Thu"],
                [5, "Fri"], [6, "Sat"], [7, "Sun"],
              ].map(([iso, label]) => {
                const on = weekdays.includes(iso);
                return (
                  <button
                    key={iso}
                    type="button"
                    onClick={() => toggleWeekday(iso)}
                    data-testid={`bf-weekday-${iso}-${item._uid}`}
                    className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition ${
                      on
                        ? "bg-[#f47a20]/15 border-[#f47a20]/50 text-[#f47a20]"
                        : "border-white/10 text-zinc-400 hover:text-zinc-200"
                    }`}
                  >
                    {label}
                  </button>
                );
              })}
              {weekdays.length > 0 && item.frequency_per_week
                && weekdays.length !== item.frequency_per_week && (
                <span className="text-[10px] text-amber-400 ml-2 self-center">
                  {weekdays.length} day{weekdays.length === 1 ? "" : "s"} picked ·
                  frequency says {item.frequency_per_week}/wk
                </span>
              )}
            </div>
          </div>
          <label className="flex flex-col gap-1 col-span-2 md:col-span-2">
            <span className="fl-label">Side</span>
            <select
              className="fl-input"
              value={item.side || ""}
              onChange={(e) => onPatch({ side: e.target.value || null })}
              data-testid={`bf-side-${item._uid}`}
            >
              <option value="">Not applicable</option>
              <option value="both">Both sides</option>
              <option value="left">Left only</option>
              <option value="right">Right only</option>
              <option value="alternating">Alternating</option>
            </select>
          </label>
        </div>
      )}

      {replaceOpen && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={() => setReplaceOpen(false)}>
          <div className="fl-card p-4 w-full max-w-lg max-h-[80vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid={`replace-modal-${item._uid}`}>
            <div className="flex items-center justify-between mb-3">
              <div className="fl-heading text-lg">Replace "{ex.name}"</div>
              <button onClick={() => setReplaceOpen(false)}><X size={16} /></button>
            </div>
            <input className="fl-input mb-3" placeholder="Search exercises…" value={replaceQuery} onChange={(e) => setReplaceQuery(e.target.value)} autoFocus data-testid={`replace-search-${item._uid}`} />
            <div className="divide-y" style={{ borderColor: "var(--fl-border)" }}>
              {replaceFiltered.map((e) => (
                <button
                  key={e.id}
                  onClick={() => { onReplace(e); setReplaceOpen(false); }}
                  className="w-full text-left py-2 hover:bg-white/5 px-2 flex items-center justify-between"
                  data-testid={`replace-pick-${e.id}`}
                >
                  <div>
                    <div className="text-sm">{e.name}</div>
                    <div className="text-[10px] text-zinc-500">{e.category}{e.equipment ? ` · ${e.equipment}` : ""}</div>
                  </div>
                  <Check size={12} className="text-[#f47a20] opacity-0 group-hover:opacity-100" />
                </button>
              ))}
              {replaceFiltered.length === 0 && <div className="text-sm text-zinc-500 p-4">No matches.</div>}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function FieldNum({ label, value, onChange, testid }) {
  return (
    <label className="flex flex-col gap-1">
      <span className="fl-label">{label}</span>
      <input type="number" className="fl-input" value={value ?? ""} onChange={(e) => onChange(e.target.value === "" ? "" : parseInt(e.target.value) || 0)} data-testid={testid} />
    </label>
  );
}
function FieldStr({ label, value, onChange, placeholder, testid }) {
  return (
    <label className="flex flex-col gap-1">
      <span className="fl-label">{label}</span>
      <input className="fl-input" value={value || ""} placeholder={placeholder} onChange={(e) => onChange(e.target.value)} data-testid={testid} />
    </label>
  );
}
function FieldTextarea({ label, value, onChange, placeholder, testid, className = "" }) {
  return (
    <label className={`flex flex-col gap-1 ${className}`}>
      <span className="fl-label">{label}</span>
      <textarea rows={2} className="fl-input" value={value || ""} placeholder={placeholder} onChange={(e) => onChange(e.target.value)} data-testid={testid} />
    </label>
  );
}
